import { z } from 'zod';

import {
  DateOnlySchema,
  IsoDateTimeSchema,
  IssueTypeSchema,
  PrioritySchema,
  ResolutionSchema,
  StatusCategorySchema,
  SwimlaneSchema,
  UuidSchema,
} from './common.js';
import { UserRefSchema } from './user.js';
import { IssueComponentRefSchema } from './component.js';

/** Ссылка на задачу в компактном виде. */
export const IssueRefSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  summary: z.string(),
  type: IssueTypeSchema,
});
export type IssueRef = z.infer<typeof IssueRefSchema>;

export const IssueStatusSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  category: StatusCategorySchema,
});

export const IssueEpicRefSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  summary: z.string(),
  type: IssueTypeSchema,
});

export const IssueSprintRefSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  state: z.enum(['future', 'active', 'closed']),
  startDate: DateOnlySchema.nullable(),
  endDate: DateOnlySchema.nullable(),
});
export type IssueSprintRef = z.infer<typeof IssueSprintRefSchema>;

/** Компактная карточка для доски (docs/05 §7). */
export const IssueCardSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  type: IssueTypeSchema,
  summary: z.string(),
  priority: PrioritySchema,
  statusId: UuidSchema,
  assignee: UserRefSchema.nullable(),
  storyPoints: z.number().nullable(),
  labels: z.array(z.string()),
  dueDate: DateOnlySchema.nullable(),
  sprintId: UuidSchema.nullable(),
  epic: IssueEpicRefSchema.nullable(),
  parentKey: z.string().nullable(),
  /** Компоненты карточки: нужны для бейджей на доске (docs/13 §7). */
  components: z.array(IssueComponentRefSchema),
  subtasks: z.object({ done: z.number().int(), total: z.number().int() }),
  hasAttachments: z.boolean(),
  commentsCount: z.number().int(),
  rank: z.string(),
  isOverdue: z.boolean(),
  updatedAt: IsoDateTimeSchema,
});
export type IssueCard = z.infer<typeof IssueCardSchema>;

/** Полное представление задачи. */
export const IssueSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  number: z.number().int(),
  projectId: UuidSchema,
  projectKey: z.string(),
  type: IssueTypeSchema,
  summary: z.string(),
  description: z.string(),
  status: IssueStatusSchema,
  priority: PrioritySchema,
  resolution: ResolutionSchema.nullable(),
  assignee: UserRefSchema.nullable(),
  reporter: UserRefSchema,
  parent: IssueRefSchema.nullable(),
  epic: IssueEpicRefSchema.nullable(),
  subtasks: z.array(IssueCardSchema),
  storyPoints: z.number().nullable(),
  dueDate: DateOnlySchema.nullable(),
  sprint: IssueSprintRefSchema.nullable(),
  labels: z.array(z.string()),
  /** Компоненты задачи: подсистемы проекта (docs/13 §4). */
  components: z.array(IssueComponentRefSchema),
  rank: z.string(),
  originalEstimate: z.number().int().nullable(),
  timeSpent: z.number().int(),
  attachmentsCount: z.number().int(),
  commentsCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Issue = z.infer<typeof IssueSchema>;

export const CreateIssueBodySchema = z
  .object({
    type: IssueTypeSchema.default('task'),
    summary: z.string().min(1, 'Введите заголовок').max(255),
    description: z.string().max(50_000).default(''),
    priority: PrioritySchema.default('medium'),
    assigneeId: UuidSchema.nullable().default(null),
    parentId: UuidSchema.nullable().default(null),
    epicId: UuidSchema.nullable().default(null),
    storyPoints: z.number().min(0).max(100).nullable().default(null),
    dueDate: DateOnlySchema.nullable().default(null),
    sprintId: UuidSchema.nullable().default(null),
    labels: z.array(z.string().max(30)).max(20).default([]),
    /** Компоненты в адресации по id (docs/13 §4): справочник грузит UI. */
    components: z.array(UuidSchema).max(10).default([]),
  })
  .strict();
export type CreateIssueBody = z.infer<typeof CreateIssueBodySchema>;

export const UpdateIssueBodySchema = z
  .object({
    summary: z.string().min(1).max(255).optional(),
    description: z.string().max(50_000).optional(),
    priority: PrioritySchema.optional(),
    assigneeId: UuidSchema.nullable().optional(),
    epicId: UuidSchema.nullable().optional(),
    storyPoints: z.number().min(0).max(100).nullable().optional(),
    dueDate: DateOnlySchema.nullable().optional(),
    sprintId: UuidSchema.nullable().optional(),
    labels: z.array(z.string().max(30)).max(20).optional(),
    /** Полная замена набора компонентов; пустой массив снимает все. */
    components: z.array(UuidSchema).max(10).optional(),
    resolution: ResolutionSchema.nullable().optional(),
    originalEstimate: z.number().int().min(0).nullable().optional(),
    timeSpent: z.number().int().min(0).optional(),
  })
  .strict();
export type UpdateIssueBody = z.infer<typeof UpdateIssueBodySchema>;

export const TransitionIssueBodySchema = z
  .object({
    toStatusId: UuidSchema,
    resolution: ResolutionSchema.nullable().default(null),
  })
  .strict();
export type TransitionIssueBody = z.infer<typeof TransitionIssueBodySchema>;

export const AvailableTransitionSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  toStatus: IssueStatusSchema,
  requireResolution: z.boolean(),
});
export type AvailableTransition = z.infer<typeof AvailableTransitionSchema>;

export const TransitionIssueResponseSchema = z.object({
  issue: IssueSchema,
  changelog: z.array(
    z.object({
      field: z.string(),
      oldValue: z.unknown(),
      newValue: z.unknown(),
    }),
  ),
});
export type TransitionIssueResponse = z.infer<typeof TransitionIssueResponseSchema>;

export const RankBodySchema = z
  .object({
    beforeId: UuidSchema.nullable().default(null),
    afterId: UuidSchema.nullable().default(null),
  })
  .strict();
export type RankBody = z.infer<typeof RankBodySchema>;

export const DuplicateIssueBodySchema = z
  .object({
    includeSubtasks: z.boolean().default(false),
  })
  .strict();

export const ListIssuesQuerySchema = z.object({
  jql: z.string().max(2000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListIssuesQuery = z.infer<typeof ListIssuesQuerySchema>;

export const ValidateJqlBodySchema = z.object({ jql: z.string().max(2000) }).strict();

export const JqlValidationSchema = z.object({
  valid: z.boolean(),
  error: z
    .object({
      message: z.string(),
      position: z.number().int(),
    })
    .optional(),
  fields: z.array(z.string()).optional(),
});
export type JqlValidation = z.infer<typeof JqlValidationSchema>;

export const BoardQuerySchema = z.object({
  /** Конкретная доска проекта; без него сервер берёт доску по умолчанию. */
  boardId: UuidSchema.optional(),
  swimlane: SwimlaneSchema.default('none'),
  sprintId: UuidSchema.optional(),
  assigneeId: z.union([UuidSchema, z.literal('none')]).optional(),
  label: z.string().max(30).optional(),
  q: z.string().max(100).optional(),
  epicId: UuidSchema.optional(),
  /** Фильтр по компоненту: id из справочника проекта (docs/13 §4). */
  componentId: UuidSchema.optional(),
  limitPerColumn: z.coerce.number().int().min(1).max(200).default(50),
});
export type BoardQuery = z.infer<typeof BoardQuerySchema>;

export const BoardLaneSchema = z.object({
  key: z.string(),
  title: z.string(),
  count: z.number().int(),
  issues: z.array(IssueCardSchema),
  hasMore: z.boolean(),
});
export type BoardLane = z.infer<typeof BoardLaneSchema>;

export const BoardColumnSchema = z.object({
  status: IssueStatusSchema,
  wipLimit: z.number().int().nullable(),
  issueCount: z.number().int(),
  lanes: z.array(BoardLaneSchema),
});
export type BoardColumn = z.infer<typeof BoardColumnSchema>;

export const BoardSchema = z.object({
  /** Какая доска проекта отрисована: имя нужно шапке и заголовку вкладки. */
  board: z.object({
    id: UuidSchema,
    name: z.string(),
    isDefault: z.boolean(),
    /** Workflow, чьи статусы стали колонками (docs/03 §1). */
    workflowId: UuidSchema,
    /**
     * Компоненты, которые показывает доска (docs/13 §7). Пустой список —
     * «все компоненты проекта»: тулбар подписывает это состояние словами.
     */
    componentIds: z.array(UuidSchema),
  }),
  columns: z.array(BoardColumnSchema),
  total: z.number().int(),
  swimlane: SwimlaneSchema,
  generatedAt: IsoDateTimeSchema,
});
export type Board = z.infer<typeof BoardSchema>;

/**
 * Карточка персональной доски (E18): задача одного проекта в общем списке.
 * Проект и фактический статус нужны, потому что колонки доски — категории,
 * а статусы у проектов свои.
 */
export const PersonalBoardIssueSchema = IssueCardSchema.extend({
  project: z.object({
    id: UuidSchema,
    key: z.string(),
    name: z.string(),
    isArchived: z.boolean(),
  }),
  status: IssueStatusSchema,
});
export type PersonalBoardIssue = z.infer<typeof PersonalBoardIssueSchema>;

export const PersonalBoardColumnSchema = z.object({
  category: StatusCategorySchema,
  /** Всего задач категории, включая не попавшие в `issues` из-за лимита. */
  count: z.number().int(),
  issues: z.array(PersonalBoardIssueSchema),
});
export type PersonalBoardColumn = z.infer<typeof PersonalBoardColumnSchema>;

export const PersonalBoardSchema = z.object({
  columns: z.array(PersonalBoardColumnSchema),
  total: z.number().int(),
  limitPerColumn: z.number().int(),
  generatedAt: IsoDateTimeSchema,
});
export type PersonalBoard = z.infer<typeof PersonalBoardSchema>;

export const PersonalBoardQuerySchema = z.object({
  q: z.string().max(100).optional(),
  limitPerColumn: z.coerce.number().int().min(1).max(200).default(50),
});
export type PersonalBoardQuery = z.infer<typeof PersonalBoardQuerySchema>;

export const BacklogIssueGroupSchema = z.object({
  epic: IssueEpicRefSchema.nullable(),
  issues: z.array(IssueCardSchema),
  totalPoints: z.number(),
  doneCount: z.number().int(),
});
export type BacklogIssueGroup = z.infer<typeof BacklogIssueGroupSchema>;

export const BacklogSchema = z.object({
  backlog: z.array(IssueCardSchema),
  sprintGroups: z.array(
    z.object({
      sprint: IssueSprintRefSchema,
      goal: z.string().nullable(),
      issues: z.array(IssueCardSchema),
      totalPoints: z.number(),
      doneCount: z.number().int(),
      completedAt: IsoDateTimeSchema.nullable(),
    }),
  ),
  epicGroups: z.array(BacklogIssueGroupSchema),
  /** Всего задач в бэклоге проекта (без подзадач) — для «показаны первые N». */
  total: z.number().int(),
  /** Есть ли задачи за пределами отданной порции. */
  hasMore: z.boolean(),
  /** Сколько задач отдано в этой порции (для «Показать ещё»). */
  limit: z.number().int(),
  offset: z.number().int(),
});

/** Параметры бэклога: порционный просмотр длинных списков (docs/05 §7). */
export const BacklogQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});
export type BacklogQuery = z.infer<typeof BacklogQuerySchema>;
export type Backlog = z.infer<typeof BacklogSchema>;

export const CommentSchema = z.object({
  id: UuidSchema,
  issueId: UuidSchema,
  author: UserRefSchema,
  parentId: UuidSchema.nullable(),
  body: z.string(),
  mentions: z.array(UserRefSchema),
  editedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Comment = z.infer<typeof CommentSchema>;

export const CreateCommentBodySchema = z
  .object({
    body: z.string().min(1, 'Комментарий не может быть пустым').max(10_000),
    parentId: UuidSchema.nullable().default(null),
  })
  .strict();
export type CreateCommentBody = z.infer<typeof CreateCommentBodySchema>;

export const UpdateCommentBodySchema = z
  .object({ body: z.string().min(1).max(10_000) })
  .strict();
export type UpdateCommentBody = z.infer<typeof UpdateCommentBodySchema>;

export const AttachmentSchema = z.object({
  id: UuidSchema,
  issueId: UuidSchema,
  uploader: UserRefSchema,
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type Attachment = z.infer<typeof AttachmentSchema>;

export const ChangelogEntrySchema = z.object({
  id: z.number().int(),
  actor: UserRefSchema.nullable(),
  field: z.string(),
  oldValue: z.unknown(),
  newValue: z.unknown(),
  createdAt: IsoDateTimeSchema,
});
export type ChangelogEntry = z.infer<typeof ChangelogEntrySchema>;

export const IssueLinkSchema = z.object({
  id: UuidSchema,
  type: z.enum(['blocks', 'relates', 'duplicates', 'clones']),
  direction: z.enum(['outward', 'inward']),
  issue: IssueRefSchema,
});
export type IssueLink = z.infer<typeof IssueLinkSchema>;

export const CreateIssueLinkBodySchema = z
  .object({
    targetIssueId: UuidSchema,
    type: z.enum(['blocks', 'relates', 'duplicates', 'clones']),
  })
  .strict();
export type CreateIssueLinkBody = z.infer<typeof CreateIssueLinkBodySchema>;

export const SavedFilterSchema = z.object({
  id: UuidSchema,
  ownerId: UuidSchema,
  projectId: UuidSchema.nullable(),
  name: z.string(),
  jql: z.string(),
  isShared: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type SavedFilter = z.infer<typeof SavedFilterSchema>;

export const CreateSavedFilterBodySchema = z
  .object({
    name: z.string().min(1, 'Введите название').max(100),
    jql: z.string().min(1).max(2000),
    projectId: UuidSchema.nullable().default(null),
    isShared: z.boolean().default(false),
  })
  .strict();
export type CreateSavedFilterBody = z.infer<typeof CreateSavedFilterBodySchema>;
export type SaveFilterInput = z.input<typeof CreateSavedFilterBodySchema>;

export const BurndownSchema = z.object({
  sprint: IssueSprintRefSchema,
  unit: z.enum(['points', 'issues']),
  ideal: z.array(z.object({ date: DateOnlySchema, value: z.number() })),
  actual: z.array(
    z.object({
      date: DateOnlySchema,
      value: z.number(),
      completed: z.number(),
      scopeChange: z.number(),
    }),
  ),
  totals: z.object({
    committed: z.number(),
    completed: z.number(),
    added: z.number(),
    removed: z.number(),
  }),
});
export type Burndown = z.infer<typeof BurndownSchema>;

export const ProjectSummarySchema = z.object({
  total: z.number().int(),
  open: z.number().int(),
  done: z.number().int(),
  byStatus: z.array(
    z.object({
      status: IssueStatusSchema,
      count: z.number().int(),
      points: z.number(),
    }),
  ),
  byAssignee: z.array(
    z.object({
      user: UserRefSchema.nullable(),
      count: z.number().int(),
      points: z.number(),
    }),
  ),
  createdLast30: z.number().int(),
  doneLast30: z.number().int(),
});
export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;
